/**
 * DI-3-S68 F14 — EVERY upload this tab drives, in one place: the running
 * drop's (`intakeRunner`) and the upload lane's (DI-3-S40 B′ — "Drop the
 * next folder" hands a set's unfinished upload to the lane, which finishes
 * it beside the next drop). Both send through the ONE tab-wide queue
 * (`intakeRunner.uploads.queue`), so "Pause uploads" — the queue's pause —
 * holds them all; the paused state is read from the queue itself (never a
 * flag a new drop could reset while the queue stays paused).
 *
 * The bottom pill (`wizard/IntakeStatusBar.tsx`) counts both; the bottom box
 * offers Pause / Resume whenever either uploads. Nothing new is persisted.
 */
import { useSyncExternalStore } from "react";
import { useStore } from "zustand";
import { intakeRunner, type IntakeRunner } from "../runner/intakeRunner.js";
import type { RunnerSet } from "../runner/runnerStore.js";
import { uploadLane, type UploadLane } from "../runner/uploadLane.js";
import { uploadStartable } from "../runner/uploadPending.js";
import type { UploadQueue } from "./queue.js";

const NO_SETS: readonly RunnerSet[] = [];

/** Is the tab-wide queue paused? (reactive) */
export function useQueuePaused(queue: UploadQueue): boolean {
  return useSyncExternalStore(
    (fn) => queue.subscribe(fn),
    () => queue.paused,
    () => false,
  );
}

/** The sets of the upload lane of this tab (empty when it holds none). */
export function useLaneUploadSets(
  lane: UploadLane | null = uploadLane,
): readonly RunnerSet[] {
  return useStore(lane?.state ?? EMPTY_STATE, (s) => s.sets);
}

const EMPTY_STATE = {
  getState: () => ({ sets: NO_SETS }),
  getInitialState: () => ({ sets: NO_SETS }),
  subscribe: () => () => undefined,
  setState: () => undefined,
} as unknown as UploadLane["state"];

/** Does any of `sets` still send (or wait to send) bytes from this tab? */
export function anyUploading(sets: readonly RunnerSet[]): boolean {
  return sets.some((s) => {
    const u = s.upload;
    if (!u || !uploadStartable(s)) return false;
    return (
      u.phase === "queued" ||
      u.phase === "uploading" ||
      u.phase === "committing"
    );
  });
}

/** Pause / resume every upload of this tab (the shared queue). */
export function pauseTabUploads(runner: IntakeRunner = intakeRunner): void {
  runner.pauseUploads();
}

export function resumeTabUploads(runner: IntakeRunner = intakeRunner): void {
  runner.resumeUploads();
}
